import React, { useState, useEffect } from 'react';
import { User, Shield, Key, Users, Globe, Cpu, ChevronRight } from 'lucide-react';
import TeamSettings from '../components/TeamSettings';
import SSOSettings from '../components/SSOSettings';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SettingsPage = () => {
  const [tab, setTab] = useState('profile');
  const [user, setUser] = useState(null);
  const [llmRouting, setLlmRouting] = useState(null);

  useEffect(() => {
    fetch(`${API_URL}/api/auth/me`, { credentials: 'include' })
      .then(r => r.json()).then(setUser).catch(() => {});
    fetch(`${API_URL}/api/settings/llm-routing`, { credentials: 'include' })
      .then(r => r.json()).then(setLlmRouting).catch(() => {});
  }, []);

  const tabs = [
    { id: 'profile', label: 'Profile', icon: User },
    { id: 'team', label: 'Team', icon: Users },
    { id: 'sso', label: 'SSO', icon: Globe },
    { id: 'llm', label: 'LLM Routing', icon: Cpu },
    { id: 'security', label: 'Security', icon: Shield },
  ];

  return (
    <div style={{ maxWidth: '900px', margin: '0 auto', padding: '1.5rem' }}>
      <h1 style={{ fontSize: '1.25rem', fontWeight: 700, marginBottom: '1rem', fontFamily: 'Space Grotesk, sans-serif' }}>Settings</h1>

      {/* Tab bar */}
      <div style={{ display: 'flex', gap: '0.25rem', borderBottom: '1px solid hsl(var(--border))', marginBottom: '1.5rem', overflowX: 'auto' }}>
        {tabs.map(t => (
          <button
            key={t.id}
            data-testid={`settings-tab-${t.id}`}
            onClick={() => setTab(t.id)}
            style={{
              padding: '0.5rem 0.75rem', background: 'none', border: 'none', cursor: 'pointer',
              borderBottom: tab === t.id ? '2px solid hsl(var(--accent))' : '2px solid transparent',
              color: tab === t.id ? 'hsl(var(--foreground))' : 'hsl(var(--muted-foreground))',
              display: 'flex', alignItems: 'center', gap: '0.375rem', fontSize: '0.8125rem', fontWeight: 500,
              whiteSpace: 'nowrap',
            }}
          >
            <t.icon size={14} /> {t.label}
          </button>
        ))}
      </div>

      {/* Profile */}
      {tab === 'profile' && user && (
        <div className="card" data-testid="settings-profile">
          <h2 style={{ fontSize: '0.9375rem', fontWeight: 600, marginBottom: '1rem' }}>Profile</h2>
          <div style={{ display: 'grid', gap: '0.75rem' }}>
            <div>
              <label style={{ fontSize: '0.75rem', color: 'hsl(var(--muted-foreground))', display: 'block', marginBottom: '0.25rem' }}>Email</label>
              <p style={{ fontSize: '0.875rem' }}>{user.email}</p>
            </div>
            <div>
              <label style={{ fontSize: '0.75rem', color: 'hsl(var(--muted-foreground))', display: 'block', marginBottom: '0.25rem' }}>Plan</label>
              <span style={{
                display: 'inline-block', padding: '0.25rem 0.5rem', borderRadius: '0.25rem', fontSize: '0.75rem', fontWeight: 600, textTransform: 'uppercase',
                background: 'hsl(var(--accent) / 0.15)', color: 'hsl(var(--accent))',
              }}>{user.plan || 'Free'}</span>
            </div>
            <div>
              <label style={{ fontSize: '0.75rem', color: 'hsl(var(--muted-foreground))', display: 'block', marginBottom: '0.25rem' }}>User ID</label>
              <code style={{ fontSize: '0.75rem', opacity: 0.7 }}>{user.user_id}</code>
            </div>
          </div>
        </div>
      )}

      {/* Team */}
      {tab === 'team' && (
        <div className="card" data-testid="settings-team">
          <h2 style={{ fontSize: '0.9375rem', fontWeight: 600, marginBottom: '1rem' }}>Team Management</h2>
          <TeamSettings />
        </div>
      )}

      {/* SSO */}
      {tab === 'sso' && (
        <div className="card" data-testid="settings-sso">
          <h2 style={{ fontSize: '0.9375rem', fontWeight: 600, marginBottom: '1rem' }}>Single Sign-On (SSO)</h2>
          <SSOSettings />
        </div>
      )}

      {/* LLM Routing */}
      {tab === 'llm' && (
        <div className="card" data-testid="settings-llm">
          <h2 style={{ fontSize: '0.9375rem', fontWeight: 600, marginBottom: '1rem' }}>Private LLM Routing</h2>
          {llmRouting ? (
            <div style={{ display: 'grid', gap: '0.75rem' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.75rem', background: 'hsl(var(--secondary))', borderRadius: '0.375rem' }}>
                <span style={{ fontSize: '0.8125rem' }}>Current Plan</span>
                <span style={{
                  padding: '0.25rem 0.5rem', borderRadius: '0.25rem', fontSize: '0.75rem', fontWeight: 600, textTransform: 'uppercase',
                  background: 'hsl(var(--accent) / 0.15)', color: 'hsl(var(--accent))',
                }}>{llmRouting.plan || 'Free'}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.75rem', background: 'hsl(var(--secondary))', borderRadius: '0.375rem' }}>
                <span style={{ fontSize: '0.8125rem' }}>Model</span>
                <code style={{ fontSize: '0.75rem' }}>{llmRouting.routing?.model}</code>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.75rem', background: 'hsl(var(--secondary))', borderRadius: '0.375rem' }}>
                <span style={{ fontSize: '0.8125rem' }}>Routing Type</span>
                <span style={{ fontSize: '0.8125rem', fontWeight: 600, color: llmRouting.routing?.is_dedicated ? '#22c55e' : 'hsl(var(--muted-foreground))' }}>
                  {llmRouting.routing?.label}
                </span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.75rem', background: 'hsl(var(--secondary))', borderRadius: '0.375rem' }}>
                <span style={{ fontSize: '0.8125rem' }}>Max Tokens</span>
                <span style={{ fontSize: '0.8125rem' }}>{llmRouting.routing?.max_tokens?.toLocaleString()}</span>
              </div>
              {llmRouting.routing?.is_private && (
                <div style={{
                  padding: '0.625rem 0.75rem', borderRadius: '0.375rem', fontSize: '0.8125rem',
                  background: 'hsl(142 70% 15%)', color: 'hsl(142 70% 70%)',
                  display: 'flex', alignItems: 'center', gap: '0.5rem',
                }}>
                  <Shield size={14} /> Private LLM routing active. Your requests are isolated from shared infrastructure.
                </div>
              )}
              {llmRouting.routing?.is_dedicated && !llmRouting.routing?.is_private && (
                <div style={{
                  padding: '0.625rem 0.75rem', borderRadius: '0.375rem', fontSize: '0.8125rem',
                  background: 'hsl(210 70% 15%)', color: 'hsl(210 70% 70%)',
                  display: 'flex', alignItems: 'center', gap: '0.5rem',
                }}>
                  <Cpu size={14} /> Dedicated routing enabled. Upgrade to Enterprise for full private isolation.
                </div>
              )}
              {!llmRouting.routing?.is_dedicated && (
                <div style={{
                  padding: '0.625rem 0.75rem', borderRadius: '0.375rem', fontSize: '0.8125rem',
                  background: 'hsl(var(--secondary))', color: 'hsl(var(--muted-foreground))',
                  display: 'flex', alignItems: 'center', gap: '0.5rem',
                }}>
                  <Cpu size={14} /> Shared pool. Upgrade to Team or Enterprise for dedicated/private routing.
                </div>
              )}
            </div>
          ) : (
            <p style={{ color: 'hsl(var(--muted-foreground))', fontSize: '0.8125rem' }}>Loading routing config...</p>
          )}
        </div>
      )}

      {/* Security */}
      {tab === 'security' && (
        <div className="card" data-testid="settings-security">
          <h2 style={{ fontSize: '0.9375rem', fontWeight: 600, marginBottom: '1rem' }}>Security</h2>
          <div style={{ display: 'grid', gap: '0.5rem' }}>
            {[
              { label: '2FA Authentication', desc: 'TOTP-based two-factor authentication', status: user?.twofa_enabled ? 'Enabled' : 'Not configured' },
              { label: 'API Keys', desc: 'Manage your API access keys', status: 'Configure' },
              { label: 'Session Management', desc: 'View and manage active sessions', status: 'View' },
            ].map((item, i) => (
              <div key={i} style={{
                display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.75rem',
                background: 'hsl(var(--secondary))', borderRadius: '0.375rem',
              }}>
                <div>
                  <p style={{ fontSize: '0.8125rem', fontWeight: 600 }}>{item.label}</p>
                  <p style={{ fontSize: '0.75rem', color: 'hsl(var(--muted-foreground))' }}>{item.desc}</p>
                </div>
                <span style={{ fontSize: '0.75rem', color: 'hsl(var(--accent))' }}>{item.status} <ChevronRight size={12} style={{ display: 'inline' }} /></span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default SettingsPage;
